package top.wcpe.allincore.dsl.ir.model

import kotlin.jvm.JvmStatic

/**
 * 属性绑定 IR 节点。
 *
 * 三种绑定类型互斥：
 *  - [BindingType.LITERAL] —— 字面量值，存放在 [literalValue]（允许 null）。
 *  - [BindingType.EXPR] —— 表达式 AST 序列化后的字节流，存放在 exprBytes。
 *  - [BindingType.EVENT] —— 事件绑定，存放目标 action 名 [eventActionName]。
 */
class UiBinding private constructor(
    // val 属性生成 getType() / getLiteralValue() / getEventActionName()，与原 Java getter 名一致。
    val type: BindingType,
    val literalValue: Any?,
    exprBytes: ByteArray?,
    val eventActionName: String?,
) {

    /** 绑定类型枚举。 */
    enum class BindingType {
        /** 字面量。 */
        LITERAL,

        /** 表达式（M5 解释器消费）。 */
        EXPR,

        /** 事件（指向 action 名）。 */
        EVENT
    }

    // 防御式拷贝：构造时复制入参数组，避免外部数组篡改内部字节。
    private val exprBytesInternal: ByteArray? = exprBytes?.copyOf()

    /** 返回 exprBytes 的副本（避免外部篡改内部状态）。 */
    val exprBytes: ByteArray?
        get() = exprBytesInternal?.copyOf()

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiBinding) return false
        return type == other.type &&
            literalValue == other.literalValue &&
            exprBytesInternal.contentEquals(other.exprBytesInternal) &&
            eventActionName == other.eventActionName
    }

    // 逐位复刻：先 Objects.hash(type, literalValue, eventActionName)（种子 1、31 倍累乘），
    // 再 31*result + Arrays.hashCode(exprBytes)。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + type.hashCode()
        result = 31 * result + (literalValue?.hashCode() ?: 0)
        result = 31 * result + (eventActionName?.hashCode() ?: 0)
        result = 31 * result + exprBytesInternal.contentHashCode()
        return result
    }

    override fun toString(): String =
        when (type) {
            BindingType.LITERAL -> "UiBinding{LITERAL=" + literalValue + "}"
            BindingType.EXPR -> "UiBinding{EXPR,len=" + (exprBytesInternal?.size ?: 0) + "}"
            BindingType.EVENT -> "UiBinding{EVENT->" + eventActionName + "}"
        }

    companion object {
        /** 构造字面量绑定（value 允许为 null）。 */
        @JvmStatic
        fun literal(value: Any?): UiBinding = UiBinding(BindingType.LITERAL, value, null, null)

        /** 构造表达式绑定，bytes 不能为 null。 */
        @JvmStatic
        fun expr(bytes: ByteArray?): UiBinding {
            requireNotNull(bytes) { "bytes" }
            return UiBinding(BindingType.EXPR, null, bytes, null)
        }

        /** 构造事件绑定，actionName 不能为 null。 */
        @JvmStatic
        fun event(actionName: String?): UiBinding {
            requireNotNull(actionName) { "actionName" }
            return UiBinding(BindingType.EVENT, null, null, actionName)
        }
    }
}
